// src/pages/SupervisoryAssessments/index.js
import React, { useState, useEffect } from 'react';
import {
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CSpinner,
    CAlert,
    CModal,
    CModalHeader,
    CModalTitle,
    CModalBody,
    CModalFooter,
    CForm,
    CFormSelect,
    CRow, CCol,
} from '@coreui/react';
import { jwtDecode } from 'jwt-decode';
import { apiClient } from 'services/Api';
import ToastNotification from 'components/globals/ToastNotification';

import PendingAssessments from './pendingAssessments';
import Averages from './averages';
import ManagerOverview from './managerOverview';
import { getAvailableYears } from './questions';

const SupervisoryAssessmentsPage = () => {
    const token = localStorage.getItem('access_token');
    const decodedToken = token ? jwtDecode(token) : {};
    const is_director = !!decodedToken.is_director;
    const is_manager = !!decodedToken.is_manager;
    const is_staff = !!decodedToken.is_staff;

    const [generateOpen, setGenerateOpen] = useState(false);
    const currentYear = new Date().getFullYear();

    // Shared year selector for all tables (non-managers)
    const [selectedYear, setSelectedYear] = useState(currentYear);

    const [generateYear, setGenerateYear] = useState(currentYear);

    const availableYears = getAvailableYears(2019);

    const [toast, setToast] = useState(null);
    const [loadingGenerate, setLoadingGenerate] = useState(false);

    // Pending (not concluded) for director overview
    const [pendingList, setPendingList] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);

    const fetchPendingAssessments = async (year = selectedYear) => {
        if (!is_director) return;

        setLoadingPending(true);
        try {
            const response = await apiClient.get(`/supervisory-assessments/pending/?year=${year}`);
            setPendingList(response.data || []);
        } catch (err) {
            console.error('Error loading pending supervisory assessments:', err);
            setToast({
                title: 'Avaliação de Chefias',
                message: 'Erro ao carregar avaliações pendentes.',
                type: 'error',
            });
            setPendingList([]);
        } finally {
            setLoadingPending(false);
        }
    };

    useEffect(() => {
        fetchPendingAssessments(selectedYear);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedYear]);

    const handleConfirmGenerate = async (e) => {
        e.preventDefault();
        setLoadingGenerate(true);

        try {
            await apiClient.post('/supervisory-assessments/generate/', {
                year: generateYear,
            });

            setToast({
                title: 'Avaliação de chefias',
                message: 'Questionários gerados/enviados com sucesso.',
                type: 'success',
            });

            setGenerateOpen(false);
            fetchPendingAssessments(selectedYear);
        } catch (error) {
            console.error('Error generating supervisory assessments:', error);
            setToast({
                title: 'Avaliação de chefias',
                message: 'Erro ao gerar/enviar os questionários.',
                type: 'error',
            });
        } finally {
            setLoadingGenerate(false);
        }
    };


    return (
        <>
            <ToastNotification toast={toast} setToast={setToast} />

            <CModal
                visible={generateOpen}
                onClose={() => setGenerateOpen(false)}
                alignment="center"
                backdrop="static"
            >
                <CModalHeader>
                    <CModalTitle>Gerar Avaliações</CModalTitle>
                </CModalHeader>

                <CForm onSubmit={handleConfirmGenerate}>
                    <CModalBody>
                        <label className="form-label fw-semibold">Ano</label>
                        <CFormSelect
                            value={generateYear}
                            onChange={(e) => setGenerateYear(Number(e.target.value))}
                            disabled={loadingGenerate}
                        >
                            {availableYears.map((y) => (
                                <option key={`gen-${y}`} value={y}>
                                    {y}
                                </option>
                            ))}
                        </CFormSelect>

                        <div className="text-muted mt-2" style={{ fontSize: 13 }}>
                            Indique o ano para o qual pretende gerar as avaliações.
                        </div>
                    </CModalBody>

                    <CModalFooter>
                        <CButton
                            color="secondary"
                            variant="ghost"
                            onClick={() => setGenerateOpen(false)}
                            disabled={loadingGenerate}
                        >
                            Cancelar
                        </CButton>
                        <CButton
                            color="info"
                            className="text-white"
                            type="submit"
                            disabled={loadingGenerate}
                        >
                            {loadingGenerate ? <CSpinner size="sm" /> : 'Gerar'}
                        </CButton>
                    </CModalFooter>
                </CForm>
            </CModal>

            {!is_manager && (
                <CCard className="mb-4">
                    <CCardHeader>
                        <CRow className="align-items-center">
                            <CCol>
                                <strong>Avaliação de chefias</strong>
                            </CCol>
                            <CCol xs="auto">
                                <CRow className="g-2 align-items-center">
                                    <CCol xs="auto">
                                        <strong>Ano:&nbsp;</strong>
                                    </CCol>
                                    <CCol xs="auto">
                                        <CFormSelect
                                            size="sm"
                                            value={selectedYear}
                                            onChange={(e) => setSelectedYear(Number(e.target.value))}
                                        >
                                            {availableYears.map((y) => (
                                                <option key={`nm-${y}`} value={y}>
                                                    {y}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                    </CCol>
                                </CRow>
                            </CCol>
                        </CRow>
                    </CCardHeader>
                    <CCardBody className="text-muted small">
                        Seleccione o ano para filtrar todas as secções desta página.
                    </CCardBody>
                </CCard>
            )}

            {/* DIRECTOR ONLY CARD */}
            {is_director && (
                <CCard className="mb-4">
                    <CCardHeader className="d-flex justify-content-between align-items-center">
                        <h3 className="mb-0">Avaliações de Chefias</h3>
                        <CButton
                            color="info"
                            className="text-white"
                            onClick={() => setGenerateOpen(true)}
                            disabled={loadingGenerate}
                        >
                            {loadingGenerate ? <CSpinner size="sm" /> : 'Gerar Avaliações'}
                        </CButton>
                    </CCardHeader>

                    <CCardBody>
                        <h5 className="mb-3">Avaliações Pendentes</h5>

                        {loadingPending ? (
                            <div className="text-center my-3">
                                <CSpinner size="sm" /> A carregar…
                            </div>
                        ) : pendingList.length === 0 ? (
                            <CAlert color="success" className="mb-0">
                                Não existem avaliações pendentes.
                            </CAlert>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-sm table-hover align-middle">
                                    <thead>
                                        <tr>
                                            <th>Departamento</th>
                                            <th>Avaliador</th>
                                            <th>Avaliado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingList.map((item) => (
                                            <tr key={item.id}>
                                                <td>{item.department_name}</td>
                                                <td>{item.evaluator_name}</td>
                                                <td>{item.evaluatee_name}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CCardBody>
                </CCard>
            )}

            {/* Non-managers: pending to fill + their own averages */}
            {!is_manager && (
                <>
                    <PendingAssessments
                        availableYears={availableYears}
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        showYearSelector={false}
                    />
                    <Averages
                        availableYears={availableYears}
                        selectedYear={selectedYear}
                        onYearChange={setSelectedYear}
                        showYearSelector={false}
                    />
                </>
            )}

            {/* Managers: department-level overview */}
            {is_manager && (
                <ManagerOverview selectedYear={selectedYear} onYearChange={setSelectedYear} />
            )}
        </>
    );
};

export default SupervisoryAssessmentsPage;
